import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertTriangle, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  courtFormSchema,
  hasAcademyManagementRole,
  locationFormSchema,
  type ClubOperatingHourSlot,
  type Court,
  type CourtFormData,
  type Location,
  type LocationFormData,
} from '@court-sync/shared';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { ClubFeatureGate } from '@/components/club/ClubFeatureGate';
import { FacilitySettingsPanel } from '@/components/club/FacilitySettingsPanel';
import { LocationHoursOverrideSection } from '@/components/club/LocationHoursOverrideSection';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Badge,
  Button,
  Drawer,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Modal,
  PaginatedTable,
  type PaginatedTableColumn,
  type PaginationState,
} from '@/components/ui';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { useAuthStore } from '@/store/auth';
import { clubService } from '@/services/club';
import { organizationService } from '@/services/organization';
import { AcademySportMultiSelectField } from '@/components/sports/AcademySportMultiSelectField';
import { getAcademySportLabel } from '@/utils/sportPresentation';
import type { OnboardingEmbeddedPageProps } from '@/components/onboarding/onboardingEmbeddedPage';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { CLUB_PAGE_TOUR_STEP_IDS } from '@/lib/tour-constants';

type FacilityTab = 'courts' | 'locations' | 'settings';

function parseFacilityTab(value: string | null): FacilityTab {
  if (value === 'locations') return 'locations';
  if (value === 'settings') return 'settings';
  return 'courts';
}

const FACILITY_TABS: { id: FacilityTab; label: string; tabId: string }[] = [
  { id: 'courts', label: 'Courts', tabId: 'club-courts-tab' },
  { id: 'locations', label: 'Locations', tabId: 'club-locations-tab' },
  { id: 'settings', label: 'Settings', tabId: 'club-facility-settings-tab' },
];

const COURT_FORM_ID = 'club-court-form';
const LOCATION_FORM_ID = 'club-location-form';
const ITEMS_PER_PAGE = 10;

/** Empty select option means "not set" rather than an invalid enum value. */
function emptyToUndefined(value: unknown): unknown {
  return value === '' ? undefined : value;
}

function formatSurfaceLabel(surface: string | null | undefined): string {
  return surface ? surface.charAt(0).toUpperCase() + surface.slice(1) : '—';
}

function formatCourtTypeLabel(courtType: string | null | undefined): string {
  if (!courtType) return '—';
  if (courtType === 'both') return 'Singles and doubles';
  if (courtType === 'singles') return 'Singles only';
  if (courtType === 'doubles') return 'Doubles only';
  return courtType.charAt(0).toUpperCase() + courtType.slice(1);
}

/**
 * Courts and locations for club operators. Facilities are club-owned, so club-only
 * organizations (no academy) manage them here instead of the academy courts page.
 */
export default function ClubCourtManagement({ embedded = false }: OnboardingEmbeddedPageProps = {}) {
  const { currentClub, isLoading: clubLoading } = useCurrentClub();
  const { user } = useAuthStore();
  const { hasClubManagement } = useClubModuleAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseFacilityTab(searchParams.get('tab'));

  const setActiveTab = (tab: FacilityTab) => {
    if (tab === 'courts') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const showAcademyAccess = hasAcademyManagementRole(user) && hasClubManagement;
  const [courts, setCourts] = useState<Court[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [courtDrawerOpen, setCourtDrawerOpen] = useState(false);
  const [editingCourt, setEditingCourt] = useState<Court | null>(null);
  const [courtPendingDelete, setCourtPendingDelete] = useState<Court | null>(null);
  const [courtsPage, setCourtsPage] = useState(1);

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [locationPendingDelete, setLocationPendingDelete] = useState<Location | null>(null);
  const [locationsPage, setLocationsPage] = useState(1);
  const [locationHoursOverride, setLocationHoursOverride] = useState<ClubOperatingHourSlot[] | null>(null);

  const courtForm = useForm<CourtFormData>({
    resolver: zodResolver(courtFormSchema),
    defaultValues: {
      name: '',
      isActive: true,
      availableForHire: false,
      locationId: '',
      academySportIds: [],
    },
  });

  const { data: organizationSports = [], isLoading: organizationSportsLoading } = useQuery({
    queryKey: ['organizationSports', currentClub?.organizationId],
    queryFn: async () => {
      const res = await organizationService.getOrganizationSports(currentClub!.organizationId);
      return res.success && res.data ? res.data : [];
    },
    enabled: !!currentClub?.organizationId,
  });

  const primaryOrganizationSportId = useMemo(() => {
    const active = organizationSports.filter((sport) => sport.isActive);
    return (
      active.find((sport) => sport.isPrimary)?.id ?? active[0]?.id
    );
  }, [organizationSports]);

  const sportLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const sport of organizationSports) {
      map.set(sport.id, getAcademySportLabel(sport));
    }
    return map;
  }, [organizationSports]);

  const locationForm = useForm<LocationFormData>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      name: '',
      address: '',
      googleMapsPlaceId: '',
      googleMapsAddress: '',
      isActive: true,
      sortOrder: 0,
    },
  });

  const loadFacilities = useCallback(async () => {
    if (!currentClub) return;
    setLoading(true);
    try {
      const [courtsRes, locationsRes] = await Promise.all([
        clubService.getFacilityCourts(currentClub.id, true),
        clubService.getFacilityLocations(currentClub.id, true),
      ]);
      if (!courtsRes.success) {
        toast.error(courtsRes.error || 'Failed to load courts');
      }
      if (!locationsRes.success) {
        toast.error(locationsRes.error || 'Failed to load locations');
      }
      setCourts(courtsRes.success && courtsRes.data ? courtsRes.data : []);
      setLocations(locationsRes.success && locationsRes.data ? locationsRes.data : []);
    } catch {
      toast.error('Failed to load courts and locations');
    } finally {
      setLoading(false);
    }
  }, [currentClub]);

  useEffect(() => {
    void loadFacilities();
  }, [loadFacilities]);

  useEffect(() => {
    setCourtsPage(1);
    setLocationsPage(1);
  }, [currentClub?.id]);

  const locationById = useMemo(
    () => Object.fromEntries(locations.map((loc) => [loc.id, loc] as const)),
    [locations]
  );

  const courtCountByLocationId = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const court of courts) {
      if (court.locationId) {
        counts[court.locationId] = (counts[court.locationId] ?? 0) + 1;
      }
    }
    return counts;
  }, [courts]);

  const sortedCourts = useMemo(
    () => [...courts].sort((a, b) => a.name.localeCompare(b.name)),
    [courts]
  );

  const sortedLocations = useMemo(
    () =>
      [...locations].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)
      ),
    [locations]
  );

  const activeLocations = useMemo(() => sortedLocations.filter((l) => l.isActive), [sortedLocations]);

  const openCreateCourt = () => {
    setEditingCourt(null);
    courtForm.reset({
      name: '',
      isActive: true,
      availableForHire: false,
      locationId: '',
      academySportIds: primaryOrganizationSportId ? [primaryOrganizationSportId] : [],
    });
    setCourtDrawerOpen(true);
  };

  const openEditCourt = (court: Court) => {
    setEditingCourt(court);
    courtForm.reset({
      name: court.name,
      surfaceType: court.surfaceType,
      courtType: court.courtType,
      isActive: court.isActive,
      availableForHire: court.availableForHire ?? false,
      locationId: court.locationId ?? '',
      academySportIds:
        court.academySportIds ??
        (primaryOrganizationSportId ? [primaryOrganizationSportId] : []),
    });
    setCourtDrawerOpen(true);
  };

  const closeCourtDrawer = () => {
    setCourtDrawerOpen(false);
    setEditingCourt(null);
  };

  const submitCourt = async (data: CourtFormData) => {
    if (!currentClub) return;
    setSubmitting(true);
    try {
      const payload: CourtFormData = {
        ...data,
        academySportIds:
          data.academySportIds?.length
            ? data.academySportIds
            : primaryOrganizationSportId
              ? [primaryOrganizationSportId]
              : undefined,
      };

      const response = editingCourt
        ? await clubService.updateFacilityCourt(currentClub.id, editingCourt.id, payload)
        : await clubService.createFacilityCourt(currentClub.id, payload);

      if (!response.success) {
        toast.error(response.error || 'Failed to save court');
        return;
      }

      toast.success(editingCourt ? 'Court updated' : 'Court created');
      closeCourtDrawer();
      await loadFacilities();
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteCourt = async () => {
    if (!currentClub || !courtPendingDelete) return;
    setSubmitting(true);
    try {
      const response = await clubService.deleteFacilityCourt(
        currentClub.id,
        courtPendingDelete.id
      );
      if (!response.success) {
        toast.error(response.error || 'Failed to delete court');
        return;
      }
      toast.success('Court deleted');
      setCourtPendingDelete(null);
      await loadFacilities();
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateLocation = () => {
    setEditingLocation(null);
    locationForm.reset({
      name: '',
      address: '',
      googleMapsPlaceId: '',
      googleMapsAddress: '',
      isActive: true,
      sortOrder: locations.length,
    });
    setLocationHoursOverride(null);
    setLocationModalOpen(true);
  };

  const openEditLocation = (location: Location) => {
    setEditingLocation(location);
    locationForm.reset({
      name: location.name,
      address: location.address ?? '',
      googleMapsPlaceId: location.googleMapsPlaceId ?? '',
      googleMapsAddress: location.googleMapsAddress ?? '',
      isActive: location.isActive,
      sortOrder: location.sortOrder,
    });
    setLocationHoursOverride(location.operatingHoursOverride ?? null);
    setLocationModalOpen(true);
  };

  const closeLocationModal = () => {
    setLocationModalOpen(false);
    setEditingLocation(null);
  };

  const submitLocation = async (data: LocationFormData) => {
    if (!currentClub) return;
    setSubmitting(true);
    try {
      const response = editingLocation
        ? await clubService.updateFacilityLocation(currentClub.id, editingLocation.id, {
            ...data,
            operatingHoursOverride: locationHoursOverride,
          })
        : await clubService.createFacilityLocation(currentClub.id, {
            ...data,
            operatingHoursOverride: locationHoursOverride,
          });

      if (!response.success) {
        toast.error(response.error || 'Failed to save location');
        return;
      }

      toast.success(editingLocation ? 'Location updated' : 'Location created');
      closeLocationModal();
      await loadFacilities();
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteLocation = async () => {
    if (!currentClub || !locationPendingDelete) return;
    setSubmitting(true);
    try {
      const response = await clubService.deleteFacilityLocation(
        currentClub.id,
        locationPendingDelete.id
      );
      if (!response.success) {
        toast.error(response.error || 'Failed to remove location');
        return;
      }
      toast.success('Location removed');
      setLocationPendingDelete(null);
      await loadFacilities();
    } finally {
      setSubmitting(false);
    }
  };

  const courtColumns: PaginatedTableColumn<Court>[] = [
    {
      key: 'name',
      header: 'Court',
      render: (court) => (
        <span className="font-medium text-gray-900 dark:text-white">{court.name}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (court) =>
        court.isActive ? (
          <Badge variant="success">Active</Badge>
        ) : (
          <Badge variant="secondary">Inactive</Badge>
        ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (court) => {
        const label = court.locationId
          ? (locationById[court.locationId]?.name ?? 'Unknown location')
          : locations.length > 0
            ? 'No site assigned'
            : '—';
        return <span className="text-muted-foreground">{label}</span>;
      },
    },
    {
      key: 'surface',
      header: 'Surface',
      render: (court) => formatSurfaceLabel(court.surfaceType),
    },
    {
      key: 'sports',
      header: 'Sports',
      render: (court) => {
        const ids = court.academySportIds ?? [];
        if (ids.length === 0) return <span className="text-muted-foreground">—</span>;
        return (
          <span className="text-muted-foreground">
            {ids.map((id) => sportLabelById.get(id) ?? 'Sport').join(', ')}
          </span>
        );
      },
    },
    {
      key: 'courtType',
      header: 'Type',
      render: (court) => formatCourtTypeLabel(court.courtType),
    },
    {
      key: 'hire',
      header: 'Court hire',
      render: (court) =>
        court.availableForHire ? (
          <Badge variant="success">Available</Badge>
        ) : (
          <span className="text-muted-foreground">Not offered</span>
        ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right w-[1%] whitespace-nowrap',
      render: (court) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => openEditCourt(court)}
            aria-label="Edit court"
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => setCourtPendingDelete(court)}
            className="text-red-600 hover:border-red-300 hover:text-red-700"
            aria-label="Delete court"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  const locationColumns: PaginatedTableColumn<Location>[] = [
    {
      key: 'name',
      header: 'Location',
      render: (location) => (
        <span className="font-medium text-gray-900 dark:text-white">{location.name}</span>
      ),
    },
    {
      key: 'address',
      header: 'Address',
      render: (location) => (
        <span className="line-clamp-2 max-w-[20rem] text-muted-foreground">
          {location.address || location.googleMapsAddress || '—'}
        </span>
      ),
    },
    {
      key: 'courts',
      header: 'Courts',
      className: 'whitespace-nowrap',
      render: (location) => {
        const count = courtCountByLocationId[location.id] ?? 0;
        return <span>{count === 0 ? 'No courts yet' : `${count} court${count === 1 ? '' : 's'}`}</span>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (location) =>
        location.isActive ? (
          <Badge variant="success">Active</Badge>
        ) : (
          <Badge variant="secondary">Inactive</Badge>
        ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right w-[1%] whitespace-nowrap',
      render: (location) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => openEditLocation(location)}
            aria-label="Edit location"
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => setLocationPendingDelete(location)}
            className="text-red-600 hover:border-red-300 hover:text-red-700"
            aria-label="Delete location"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  const courtsTotalPages = Math.max(1, Math.ceil(sortedCourts.length / ITEMS_PER_PAGE));
  const paginatedCourts = sortedCourts.slice(
    (courtsPage - 1) * ITEMS_PER_PAGE,
    courtsPage * ITEMS_PER_PAGE
  );

  const locationsTotalPages = Math.max(1, Math.ceil(sortedLocations.length / ITEMS_PER_PAGE));
  const paginatedLocations = sortedLocations.slice(
    (locationsPage - 1) * ITEMS_PER_PAGE,
    locationsPage * ITEMS_PER_PAGE
  );

  const pendingDeleteCourtCount =
    locationPendingDelete != null ? (courtCountByLocationId[locationPendingDelete.id] ?? 0) : 0;

  const headerAction =
    activeTab === 'courts' && !loading && sortedCourts.length > 0 ? (
      <Button
        type="button"
        size="sm"
        className="w-full md:w-auto"
        id={CLUB_PAGE_TOUR_STEP_IDS.COURTS_NEW}
        onClick={openCreateCourt}
      >
        <Plus aria-hidden />
        New court
      </Button>
    ) : activeTab === 'locations' && !loading && sortedLocations.length > 0 ? (
      <Button type="button" size="sm" className="w-full md:w-auto" onClick={openCreateLocation}>
        <Plus aria-hidden />
        Add location
      </Button>
    ) : undefined;

  if (clubLoading || !currentClub) {
    return (
      <div className="flex min-h-[240px] items-center justify-center" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (currentClub.isInternalShell) {
    return (
      <ClubFeatureGate>
        <DashboardEmptyStateCard>
          <DashboardEmptyStateIcon icon={MapPin} />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
            Courts are managed from your academy dashboard
          </h3>
          <p className="mb-4 text-gray-500 dark:text-gray-400">
            This organization uses the academy courts page for facility setup. Your existing courts
            are still there — open the academy courts view to manage them.
          </p>
          <Button asChild>
            <Link to="/admin/courts">Open academy courts</Link>
          </Button>
        </DashboardEmptyStateCard>
      </ClubFeatureGate>
    );
  }

  return (
    <ClubFeatureGate>
      <div className="contents">
        {!embedded ? (
          <PageHeader
            title="Courts & locations"
            subtitle={`${currentClub.name} — facility setup`}
            help={
              <PageFeatureHelp title="Courts and locations">
                <p>
                  Courts belong to your club. Add sites first when you operate from more than one
                  venue, then assign each court to its site. Tag courts with the sports they support
                  so court hire and scheduling filters work correctly.
                </p>
              </PageFeatureHelp>
            }
            helpAnchorId={CLUB_PAGE_TOUR_STEP_IDS.COURTS_GUIDE}
            actions={headerAction}
          />
        ) : null}

        <div id={CLUB_PAGE_TOUR_STEP_IDS.COURTS_TABS}>
          <DashboardTabNav<FacilityTab>
            role="tablist"
            tabs={FACILITY_TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
            ariaLabel="Courts and locations"
          />
        </div>

        {loading && activeTab !== 'settings' ? (
          <div className="flex min-h-[240px] items-center justify-center" aria-busy="true">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : activeTab === 'courts' ? (
          sortedCourts.length === 0 ? (
            <DashboardEmptyStateCard>
              <DashboardEmptyStateIcon icon={MapPin} />
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                No courts yet
              </h3>
              <p className="mb-4 text-gray-500 dark:text-gray-400">
                Add your courts so members and visitors can book court hire.
              </p>
              <Button id={CLUB_PAGE_TOUR_STEP_IDS.COURTS_NEW} onClick={openCreateCourt}>
                New court
              </Button>
            </DashboardEmptyStateCard>
          ) : (
            <DashboardDataCard>
              <div id={CLUB_PAGE_TOUR_STEP_IDS.COURTS_LIST}>
              <PaginatedTable<Court>
                columns={courtColumns}
                data={paginatedCourts}
                pagination={
                  {
                    page: courtsPage,
                    limit: ITEMS_PER_PAGE,
                    total: sortedCourts.length,
                    totalPages: courtsTotalPages,
                  } satisfies PaginationState
                }
                emptyMessage="No courts found."
                onPageChange={setCourtsPage}
                onRowClick={openEditCourt}
                noWrapperPadding
              />
              </div>
            </DashboardDataCard>
          )
        ) : activeTab === 'settings' && currentClub ? (
          <div id={CLUB_PAGE_TOUR_STEP_IDS.COURTS_FACILITY_SETTINGS}>
          <FacilitySettingsPanel
            club={currentClub}
            showAcademyAccess={showAcademyAccess}
            hasCourtHire={hasClubManagement}
          />
          </div>
        ) : activeTab === 'locations' && sortedLocations.length === 0 ? (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={MapPin} />
            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
              No locations yet
            </h3>
            <p className="mb-4 text-gray-500 dark:text-gray-400">
              Create locations to group courts by venue. Addresses can be synced from Google.
            </p>
            <Button onClick={openCreateLocation}>Add location</Button>
          </DashboardEmptyStateCard>
        ) : activeTab === 'locations' ? (
          <DashboardDataCard>
            <PaginatedTable<Location>
              columns={locationColumns}
              data={paginatedLocations}
              pagination={
                {
                  page: locationsPage,
                  limit: ITEMS_PER_PAGE,
                  total: sortedLocations.length,
                  totalPages: locationsTotalPages,
                } satisfies PaginationState
              }
              emptyMessage="No locations yet."
              onPageChange={setLocationsPage}
              onRowClick={openEditLocation}
              noWrapperPadding
            />
          </DashboardDataCard>
        ) : null}

        <Drawer
          isOpen={courtDrawerOpen}
          onClose={closeCourtDrawer}
          title={editingCourt ? 'Edit court' : 'New court'}
          width="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeCourtDrawer}>
                Cancel
              </Button>
              <Button type="submit" form={COURT_FORM_ID} isLoading={submitting}>
                {editingCourt ? 'Save court' : 'Create court'}
              </Button>
            </div>
          }
        >
          <form
            id={COURT_FORM_ID}
            onSubmit={courtForm.handleSubmit(submitCourt)}
            className="space-y-5"
          >
            <Field data-invalid={!!courtForm.formState.errors.name?.message}>
              <FieldLabel htmlFor="club-court-name">Court name *</FieldLabel>
              <Input
                id="club-court-name"
                aria-invalid={!!courtForm.formState.errors.name?.message}
                {...courtForm.register('name')}
                placeholder="Court 1"
              />
              <FieldError>{courtForm.formState.errors.name?.message}</FieldError>
            </Field>

            <AcademySportMultiSelectField
              sports={organizationSports}
              control={courtForm.control}
              name="academySportIds"
              disabled={submitting || organizationSportsLoading}
              emptyStateMessage="Configure sports in Organization Settings before assigning courts."
            />

            <div>
              <label
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                htmlFor="club-court-surface"
              >
                Surface type
              </label>
              <select
                id="club-court-surface"
                {...courtForm.register('surfaceType', { setValueAs: emptyToUndefined })}
                className="input"
              >
                <option value="">Select surface type</option>
                <option value="hard">Hard</option>
                <option value="clay">Clay</option>
                <option value="grass">Grass</option>
                <option value="indoor">Indoor</option>
                <option value="synthetic">Synthetic</option>
                <option value="wood">Wood</option>
                <option value="carpet">Carpet</option>
              </select>
            </div>

            <div>
              <label
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                htmlFor="club-court-type"
              >
                Court type
              </label>
              <select
                id="club-court-type"
                {...courtForm.register('courtType', { setValueAs: emptyToUndefined })}
                className="input"
              >
                <option value="">Select court type</option>
                <option value="singles">Singles only</option>
                <option value="doubles">Doubles only</option>
                <option value="both">Singles &amp; doubles</option>
              </select>
            </div>

            {activeLocations.length > 0 ? (
              <div>
                <label
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  htmlFor="club-court-location"
                >
                  Location
                </label>
                <select
                  id="club-court-location"
                  {...courtForm.register('locationId')}
                  className="input"
                >
                  <option value="">No site assigned</option>
                  {activeLocations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...courtForm.register('isActive')}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Active (usable on the facility calendar)
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...courtForm.register('availableForHire')}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Available for hire
                </span>
              </label>
            </div>
          </form>
        </Drawer>

        <Modal
          isOpen={!!courtPendingDelete}
          onClose={() => setCourtPendingDelete(null)}
          title="Delete this court?"
          maxWidth="lg"
        >
          {courtPendingDelete ? (
            <div className="space-y-4">
              <div
                role="alert"
                className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
              >
                <AlertTriangle
                  className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
                  aria-hidden
                />
                <div className="min-w-0 space-y-2">
                  <p className="font-semibold">This action is permanent and cannot be undone.</p>
                  <p>
                    Courts with court hire bookings cannot be deleted — deactivate
                    &quot;{courtPendingDelete.name}&quot; instead to take it off the schedule.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setCourtPendingDelete(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={confirmDeleteCourt}
                  isLoading={submitting}
                >
                  Delete court
                </Button>
              </div>
            </div>
          ) : null}
        </Modal>

        <Drawer
          isOpen={locationModalOpen}
          onClose={closeLocationModal}
          title={editingLocation ? 'Edit location' : 'Add location'}
          width="2xl"
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeLocationModal}>
                Cancel
              </Button>
              <Button type="submit" form={LOCATION_FORM_ID} isLoading={submitting}>
                {editingLocation ? 'Save location' : 'Create location'}
              </Button>
            </div>
          }
        >
          <form
            id={LOCATION_FORM_ID}
            onSubmit={locationForm.handleSubmit(submitLocation)}
            className="space-y-4"
          >
            <Field data-invalid={!!locationForm.formState.errors.name?.message}>
              <FieldLabel htmlFor="club-loc-name">Location name *</FieldLabel>
              <Input
                id="club-loc-name"
                aria-invalid={!!locationForm.formState.errors.name?.message}
                {...locationForm.register('name')}
                placeholder="Main site"
              />
              <FieldError>{locationForm.formState.errors.name?.message}</FieldError>
            </Field>

            <Controller
              name="address"
              control={locationForm.control}
              render={({ field }) => (
                <AddressAutocomplete
                  label="Address"
                  value={field.value ?? ''}
                  onChange={(value) => {
                    field.onChange(value);
                    locationForm.setValue('googleMapsPlaceId', '');
                    locationForm.setValue('googleMapsAddress', '');
                  }}
                  onPlaceResolved={(place) => {
                    field.onChange(place.formattedAddress);
                    locationForm.setValue('googleMapsAddress', place.formattedAddress);
                    locationForm.setValue('googleMapsPlaceId', place.placeId ?? '');
                  }}
                  placeholder="Start typing to search with Google"
                />
              )}
            />

            <div className="flex items-center gap-2">
              <input
                id="club-loc-active"
                type="checkbox"
                {...locationForm.register('isActive')}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label htmlFor="club-loc-active" className="text-sm text-gray-700 dark:text-gray-300">
                Active (selectable for courts and shown publicly)
              </label>
            </div>

            <input type="hidden" {...locationForm.register('sortOrder', { valueAsNumber: true })} />

            <LocationHoursOverrideSection
              value={locationHoursOverride}
              onChange={setLocationHoursOverride}
              disabled={submitting}
            />
          </form>
        </Drawer>

        <Modal
          isOpen={!!locationPendingDelete}
          onClose={() => setLocationPendingDelete(null)}
          title="Remove this location?"
          maxWidth="lg"
        >
          {locationPendingDelete ? (
            <div className="space-y-4">
              <div
                role="alert"
                className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
              >
                <AlertTriangle
                  className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
                  aria-hidden
                />
                <div className="min-w-0 space-y-2">
                  <p className="font-semibold">This action is permanent and cannot be undone.</p>
                  {pendingDeleteCourtCount > 0 ? (
                    <p>
                      Removing &quot;{locationPendingDelete.name}&quot; also deletes{' '}
                      <strong>
                        {pendingDeleteCourtCount} court
                        {pendingDeleteCourtCount === 1 ? '' : 's'}
                      </strong>{' '}
                      at this site. Courts with court hire bookings must be moved first.
                    </p>
                  ) : (
                    <p>
                      This removes the site &quot;{locationPendingDelete.name}&quot; from your club.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocationPendingDelete(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={confirmDeleteLocation}
                  isLoading={submitting}
                >
                  Remove location
                </Button>
              </div>
            </div>
          ) : null}
        </Modal>
      </div>
    </ClubFeatureGate>
  );
}
